import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import { filterBookingsForPeriod } from './insightsQueries';

/**
 * Die vier Abfragen der Apartment-Auswertung plus der CSV-Export.
 *
 * <p>Bewusst parallel und nicht nacheinander: Gewinn und Verlust, Ausgaben-Belege, Kennzahlen
 * und Buchungen hängen nicht voneinander ab.</p>
 */
export function usePropertyInsights(propertyId: string, periodString: string, exportYear: number) {
  const api = useApi();

  const pnl = useQuery({
    queryKey: ['finance', 'pnl', propertyId, periodString],
    queryFn: () => api.finance.pnl(propertyId, { period: periodString }),
  });
  const expenses = useQuery({
    queryKey: ['finance', 'expenses', propertyId, periodString],
    queryFn: () => api.finance.expenses(propertyId, { period: periodString }),
  });
  const insights = useQuery({
    queryKey: ['insights', 'property', propertyId, periodString],
    queryFn: () => api.insights.property(propertyId, { period: periodString }),
  });
  const bookings = useQuery({
    queryKey: ['bookings', 'list-for-insights'],
    queryFn: () => api.bookings.list({ pageSize: 500 }),
  });

  const filteredBookings = useMemo(
    () =>
      filterBookingsForPeriod(
        bookings.data?.items ?? [],
        propertyId,
        pnl.data?.periodFrom,
        pnl.data?.periodTo,
      ),
    [bookings.data, propertyId, pnl.data?.periodFrom, pnl.data?.periodTo],
  );

  const exportCsv = useMutation({
    mutationFn: () => api.finance.exportCsv(exportYear, propertyId),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `finanzen-${exportYear}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success('CSV heruntergeladen');
    },
    onError: async (err) => {
      const apiError = await toApiError(err);
      toast.error(apiError.detail ?? apiError.title);
    },
  });

  return { pnl, expenses, insights, filteredBookings, exportCsv };
}
